import { inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { IdUtils } from '@ecm/consumer';
import { Contract } from '@ecm/portfolio';
import { LinkService, ToggleService, TrackingData } from '@ecm/router';
import { KenobiParams, Status } from '@ecm/tracker';

import { ClientTypeEnum } from '../../../business/api/dashboard/forbidden/clientType.enum';
import { ForbiddenViewModelMapper } from '../../../business/mapper/home/forbidden/forbidden.view-model.mapper';
import { ButtonViewModel } from '../../../business/model/home/button.view-model';
import { ForbiddenViewModel } from '../../../business/model/home/forbidden/forbidden.view-model';
import { KenobiCodes } from '../../../business/model/tracking/kenobiCodes';
import { TrackingService } from '../../../services/tracking.service';

@Injectable({
    providedIn: 'root',
})
export class ForbiddenStoreService {
    private readonly _forbiddenViewModel$ = signal<ForbiddenViewModel>(undefined);

    private readonly pageId: string;

    protected readonly linkService = inject(LinkService);
    private readonly trackingService = inject(TrackingService);
    private readonly translate = inject(TranslateService);
    private readonly toggleService = inject(ToggleService);

    public constructor() {
        this.pageId = IdUtils.generateRandomId();
    }

    public enter(forbiddenType: ClientTypeEnum, contract?: Contract, viewModel?: ForbiddenViewModel): void {
        const translation = this.translate.instant('dashboard.forbidden');

        if (
            forbiddenType === ClientTypeEnum.PARNASSE ||
            forbiddenType === ClientTypeEnum.MULTIBAL ||
            forbiddenType === ClientTypeEnum.PRO ||
            forbiddenType === ClientTypeEnum.OBS
        ) {
            this._forbiddenViewModel$.set(viewModel);
        } else {
            this.toggleService.getFeatureStatus('DASHBOARD.ADD_CONTRACT_ECM').subscribe((ecmAddContractStatus) => {
                this._forbiddenViewModel$.set(
                    ForbiddenViewModelMapper.getForbiddenViewModel(
                        forbiddenType,
                        translation,
                        ecmAddContractStatus,
                        forbiddenType !== ClientTypeEnum.NSRU && contract ? contract : null,
                    ),
                );
                this.onAfterViewLoaded();
            });
        }
    }

    public get forbiddenViewModel(): ForbiddenViewModel {
        return this._forbiddenViewModel$();
    }

    public onAfterViewLoaded(): void {
        this.trackView();
    }

    public openLink(linkInfo: ButtonViewModel, params?: Map<string, string>): void {
        this.linkService.openLink(
            linkInfo.link,
            new TrackingData({
                tmsTracking: {
                    trackedPage: linkInfo.trackedPage,
                    trackingArea: linkInfo.trackingArea,
                    trackingName: linkInfo.trackingName,
                    targetUrl: linkInfo.hrefLink,
                },
            }),
            params,
            null,
            true,
        );
    }

    private trackView(): void {
        const kenobiCode = this.getKenobiCode(this._forbiddenViewModel$());
        this.trackingService.trackKenobi(
            new KenobiParams(kenobiCode.functionality, kenobiCode.rubric, Status.OK),
            this.pageId,
        );
    }

    private getKenobiCode(value: ForbiddenViewModel): {
        functionality: string;
        rubric: string;
    } {
        let kenobiCode = KenobiCodes.CRATTRAP;
        if (value.clientType === ClientTypeEnum.NSRU) {
            kenobiCode = KenobiCodes.ACCSCONT;
        }
        return kenobiCode;
    }
}
